import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Loader2, ExternalLink } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { adminGetMyGear, adminUpdateMyGear, matchGearLibrary } from '@/lib/api';
import { GEAR_PROFILE } from '@/constants/testIds';

const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] text-sm resize-none focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';
const lines = (arr) => (arr || []).join('\n');
const split = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean);

/** Per-photographer kit. Location Scout reads this to tailor camera settings
 *  and the pack list to what you actually own. Items that match a published
 *  Gear Library entry (by name/alias) link out to their full spec page. */
export const GearProfileCard = () => {
  const [gear, setGear] = useState(null);
  const [form, setForm] = useState({ bodies: '', lenses: '', lighting: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [linked, setLinked] = useState([]); // [{ name, slug }]

  useEffect(() => {
    adminGetMyGear()
      .then((g) => { setGear(g); setForm({ bodies: lines(g.bodies), lenses: lines(g.lenses), lighting: lines(g.lighting), notes: g.notes || '' }); })
      .catch(() => setGear({}));
  }, []);

  useEffect(() => {
    if (!gear) return;
    const names = [...(gear.bodies || []), ...(gear.lenses || []), ...(gear.lighting || [])];
    if (names.length === 0) { setLinked([]); return; }
    let cancelled = false;
    Promise.all(names.map((name) => matchGearLibrary(name).then((r) => ({ name, slug: r.slug })).catch(() => ({ name, slug: null }))))
      .then((results) => { if (!cancelled) setLinked(results.filter((r) => r.slug)); });
    return () => { cancelled = true; };
  }, [gear]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const g = await adminUpdateMyGear({ bodies: split(form.bodies), lenses: split(form.lenses), lighting: split(form.lighting), notes: form.notes.trim() });
      setGear(g);
      toast.success('Gear saved. Scout will tailor settings to your kit.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your gear.');
    } finally {
      setSaving(false);
    }
  };

  const count = gear ? (gear.bodies?.length || 0) + (gear.lenses?.length || 0) + (gear.lighting?.length || 0) : 0;

  return (
    <div className="max-w-md rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8" data-testid={GEAR_PROFILE.card}>
      <h3 className="font-display text-xl text-[hsl(var(--lxra-fg))] mb-1 flex items-center gap-2"><Camera className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> My Gear</h3>
      <p className="text-sm text-[hsl(var(--lxra-muted))] mb-2">
        Location Scout uses this to suggest settings for <em>your</em> bodies and lenses, and to build a pack list you can trust. One item per line.
      </p>
      <p data-testid={GEAR_PROFILE.summary} className="text-[11px] text-[hsl(var(--lxra-gold))] mb-6">{count > 0 ? `${count} items on file` : 'Nothing saved yet — Scout will assume a full-frame kit with 35/50/85mm primes.'}</p>
      <form onSubmit={save} className="space-y-4">
        <div className="space-y-1.5">
          <Label className={labelClass}>Camera bodies</Label>
          <Textarea rows={2} value={form.bodies} onChange={(e) => setForm({ ...form, bodies: e.target.value })} data-testid={GEAR_PROFILE.bodiesTextarea} placeholder={'Sony A7 IV\nCanon R6 Mark II'} className={fieldClass} />
        </div>
        <div className="space-y-1.5">
          <Label className={labelClass}>Lenses</Label>
          <Textarea rows={3} value={form.lenses} onChange={(e) => setForm({ ...form, lenses: e.target.value })} data-testid={GEAR_PROFILE.lensesTextarea} placeholder={'35mm f/1.4\n85mm f/1.4\n24-70mm f/2.8'} className={fieldClass} />
        </div>
        <div className="space-y-1.5">
          <Label className={labelClass}>Lighting &amp; modifiers</Label>
          <Textarea rows={2} value={form.lighting} onChange={(e) => setForm({ ...form, lighting: e.target.value })} data-testid={GEAR_PROFILE.lightingTextarea} placeholder={'Godox AD200 Pro\n5-in-1 reflector'} className={fieldClass} />
        </div>
        <div className="space-y-1.5">
          <Label className={labelClass}>Preferences</Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={GEAR_PROFILE.notesTextarea} placeholder="Natural light first; flash only at blue hour. Shoot RAW, Auto WB." className={fieldClass} />
        </div>
        <Button type="submit" disabled={saving || gear === null} data-testid={GEAR_PROFILE.saveButton} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Gear'}
        </Button>
      </form>

      {linked.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[hsl(var(--lxra-border))]">
          <Label className={labelClass}>Gear Guide references</Label>
          <div data-testid={GEAR_PROFILE.linkedList} className="mt-2 flex flex-wrap gap-1.5">
            {linked.map(({ name, slug }, idx) => (
              <Link key={`${slug}-${idx}`} to={`/gear/${slug}`} data-testid={GEAR_PROFILE.linkedItem(idx)}>
                <Badge
                  variant="secondary"
                  className="font-normal text-[11px] gap-1 hover:border-[hsl(var(--lxra-gold))]/50 hover:text-[hsl(var(--lxra-gold))] transition-colors cursor-pointer"
                >
                  {name}
                  <ExternalLink className="h-3 w-3" />
                </Badge>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

